/**
 * Vault — credenciales sin owner activo (F4, solo admin). Aparecen cuando el
 * owner único se dio de baja. El admin las reasigna a otra persona (nunca a sí
 * mismo: sin auto-escalación); la persona recibe un DM y queda en el log.
 * No renderiza nada si no hay ninguna.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { UserX } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { AreaBadge } from '@/components/vault/vault-ui';
import { errMsg } from '@/hooks/useVault';

export function OwnerlessPanel() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const list = trpc.vaultAdmin.listOwnerless.useQuery(undefined, { staleTime: 60_000 });
  const [emails, setEmails] = useState<Record<string, string>>({});

  const reassign = trpc.vaultAdmin.reassignOwner.useMutation({
    onSuccess: (_r, vars) => {
      utils.vaultAdmin.listOwnerless.invalidate();
      utils.vault.listSecrets.invalidate();
      toast.success(t('vault.ownerless.done', 'Owner reasignado a {{email}}', { email: vars.newOwnerEmail }));
      setEmails((m) => ({ ...m, [vars.secretId]: '' }));
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const items = list.data ?? [];
  if (list.isLoading || items.length === 0) return null;

  return (
    <Card className="space-y-3 border-amber-300/60 bg-amber-50/40 p-4 dark:border-amber-800/60 dark:bg-amber-950/20">
      <div className="flex items-start gap-2">
        <UserX className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
        <div>
          <h2 className="font-medium">{t('vault.ownerless.title', 'Credenciales sin owner activo')}</h2>
          <p className="text-xs text-muted-foreground">
            {t('vault.ownerless.body', 'Su owner se dio de baja. Asignales un owner nuevo (no podés ser vos): le avisamos por Slack y queda en el log.')}
          </p>
        </div>
      </div>
      <ul className="divide-y">
        {items.map((s) => {
          const email = emails[s.secretId] ?? '';
          return (
            <li key={s.secretId} className="flex flex-wrap items-center gap-2 py-2">
              <div className="min-w-0 flex-1">
                <Link to={`/vault/secrets/${s.secretId}`} className="font-medium hover:underline">{s.name}</Link>
                <div className="truncate text-xs text-muted-foreground">
                  {s.username} ·{' '}
                  {s.reason === 'sin_owner'
                    ? t('vault.ownerless.no_owner', 'sin owner')
                    : s.reason === 'owner_desconocido'
                      ? t('vault.ownerless.owner_unknown', 'owner que no está en el community map: {{owners}}', { owners: s.owners.join(', ') })
                      : t('vault.ownerless.owner_left', 'owner en baja: {{owners}}', { owners: s.owners.join(', ') })}
                </div>
              </div>
              <AreaBadge area={s.area} />
              <form
                className="flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  reassign.mutate({ secretId: s.secretId, newOwnerEmail: email.trim() });
                }}
              >
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmails((m) => ({ ...m, [s.secretId]: e.target.value }))}
                  placeholder={t('vault.ownerless.email_ph', 'nuevo.owner@winclap.com')}
                  className="h-8 w-56"
                  required
                />
                <Button type="submit" size="sm" disabled={reassign.isPending || !email.trim()}>
                  {t('vault.ownerless.assign', 'Asignar')}
                </Button>
              </form>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
